import { prepareEnvelopeAtArrival } from "~/utils/serialization"

const BINARY_CONTENT_TYPE = "application/x-trpc-bin"

// HTML instead of data/JSON — expired session or gateway error page. Typed so TrpcProvider
// can route to re-auth instead of tRPC surfacing an "Unexpected token '<'" parse error.
export class UnexpectedHtmlResponseError extends Error {
  constructor(readonly status: number) {
    super(`Server returned an HTML page instead of tRPC data (status ${status}).`)
    this.name = "UnexpectedHtmlResponseError"
  }
}

/**
 * Custom fetch wrapper for binary tRPC responses.
 *
 * Binary path: Server CBOR encodes → Zstd compresses (Brotli fallback for Safari) →
 * browser auto-decompresses → this wrapper reads bytes → CBOR decodes →
 * overrides .json() → tRPC pipeline.
 *
 * JSON path (errors, streaming): passes through unchanged.
 */
export function createBinaryFetch(): typeof globalThis.fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await globalThis.fetch(input, init)
    // Error responses come back as JSON (application/json). Pass through unchanged —
    // tRPC handles errors separately from data (no output.deserialize call on errors).

    const contentType = response.headers.get("content-type")
    // Fail typed before tRPC calls .json() on HTML (valid responses are binary or JSON).
    if (contentType?.includes("text/html")) {
      throw new UnexpectedHtmlResponseError(response.status)
    }
    if (!contentType?.includes(BINARY_CONTENT_TYPE)) {
      return response
    }

    const buffer = await response.arrayBuffer()
    const envelope = prepareEnvelopeAtArrival(new Uint8Array(buffer))

    return new Proxy(response, {
      get(target, prop, receiver) {
        if (prop === "json") return () => Promise.resolve(envelope)
        return Reflect.get(target, prop, receiver)
      },
    })
  }
}
